import { env } from '@/core/config/env';
import { ApiError, extractMessage, unwrap } from './envelope';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
const UNSAFE: Method[] = ['POST', 'PUT', 'PATCH', 'DELETE'];

// --- CSRF token (double-submit). Set from GET /admin/auth/csrf. ---
let csrfToken: string | null = null;
export const setCsrfToken = (t: string | null): void => {
  csrfToken = t;
};
export const getCsrfToken = (): string | null => csrfToken;

// --- Access token: in memory only (plan §6). Sent as Bearer; refreshed via the
//     httpOnly cookie. Never written to localStorage/sessionStorage. ---
let accessToken: string | null = null;
export const setAccessToken = (t: string | null): void => {
  accessToken = t;
};
export const getAccessToken = (): string | null => accessToken;

// --- Hooks registered by AuthProvider (avoids circular imports) ---
let onAuthFailure: (() => void) | null = null;
export const setAuthFailureHandler = (fn: (() => void) | null): void => {
  onAuthFailure = fn;
};
let refreshFn: (() => Promise<boolean>) | null = null;
export const setRefreshHandler = (fn: (() => Promise<boolean>) | null): void => {
  refreshFn = fn;
};

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the 401 -> refresh -> retry cycle (used by auth calls themselves). */
  skipAuthRetry?: boolean;
  /** Return the raw body without unwrapping the `{ success, data }` envelope. */
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.startsWith('http') ? path : `${env.adminApiBaseUrl}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<T>(method: Method, path: string, opts: RequestOptions = {}): Promise<T> {
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  // For FormData, let the browser set the multipart Content-Type (with boundary).
  if (opts.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (UNSAFE.includes(method) && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include', // send/receive the httpOnly refresh cookie
      body: opts.body === undefined ? undefined : isFormData ? (opts.body as FormData) : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (e) {
    throw new ApiError('network', 'Network error. Check your connection and try again.', undefined, e);
  }

  if (res.status === 401 && !opts.skipAuthRetry) {
    if (refreshFn && (await refreshFn())) {
      return request<T>(method, path, { ...opts, skipAuthRetry: true });
    }
    onAuthFailure?.();
    throw new ApiError('auth', 'Your session has expired. Please sign in again.', 401);
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const kind: ApiError['kind'] =
      res.status === 403
        ? 'permission'
        : res.status === 400 || res.status === 422
          ? 'validation'
          : 'server';
    throw new ApiError(kind, extractMessage(parsed), res.status, parsed);
  }

  return opts.raw ? (parsed as T) : unwrap<T>(parsed);
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};
